import React, { useEffect, useRef, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SmoothScroll from './components/SmoothScroll';
import ScrollProgress from './components/ScrollProgress';
import EstimatePopup from './components/EstimatePopup';
import FloatingActions from './components/FloatingActions';
import CookieBanner from './components/CookieBanner';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Home from './pages/Home';

/**
 * Lazy-loaded routes — code-split so visitors only download the JS for
 * the page they're actually visiting. Home stays eager because it's the
 * landing page and we want zero loading flash on first paint.
 */
const Products      = lazy(() => import('./pages/Products'));
const About         = lazy(() => import('./pages/About'));
const Installation  = lazy(() => import('./pages/Installation'));
const Projects      = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Contact       = lazy(() => import('./pages/Contact'));
const Blog          = lazy(() => import('./pages/Blog'));
const Privacy       = lazy(() => import('./pages/Privacy'));
const Terms         = lazy(() => import('./pages/Terms'));
const NotFound      = lazy(() => import('./pages/NotFound'));
const LocationLondon = lazy(() => import('./pages/locations').then(m => ({ default: m.LocationLondon })));
const LocationKent   = lazy(() => import('./pages/locations').then(m => ({ default: m.LocationKent })));
const LocationSurrey = lazy(() => import('./pages/locations').then(m => ({ default: m.LocationSurrey })));
const LocationEssex  = lazy(() => import('./pages/locations').then(m => ({ default: m.LocationEssex })));

/**
 * ScrollManager — restores scroll position when navigating via browser back/
 * forward (POP) and scrolls to top on new navigations (PUSH / REPLACE).
 *
 * Example: scroll halfway down /projects → open a project → click browser
 * back → land back at the same scroll position on /projects. Going forward
 * to a fresh URL always starts at the top.
 */
function ScrollManager() {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const positions = useRef(new Map());
  const prevPath = useRef(pathname);

  useEffect(() => {
    // Save the scroll position of the page we're leaving
    positions.current.set(prevPath.current, window.scrollY);

    if (navType === 'POP') {
      const saved = positions.current.get(pathname) ?? 0;
      // Defer to next frame so the new route's content has mounted
      requestAnimationFrame(() => window.scrollTo({ top: saved, behavior: 'instant' }));
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    prevPath.current = pathname;
  }, [pathname, navType]);

  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/services" element={<Navigate to="/products" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/installation" element={<Installation />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:slug" element={<ProjectDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/artificial-lawns-london" element={<LocationLondon />} />
        <Route path="/artificial-lawns-kent" element={<LocationKent />} />
        <Route path="/artificial-lawns-surrey" element={<LocationSurrey />} />
        <Route path="/artificial-lawns-essex" element={<LocationEssex />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollProgress />
        <Navbar />
        <ScrollManager />
        <RouteErrorBoundary>
          <AppRoutes />
        </RouteErrorBoundary>
        <Footer />
        <EstimatePopup />
        <FloatingActions />
        <CookieBanner />
      </BrowserRouter>
    </div>
  );
}

export default App;
